import { useCallback, useEffect, useMemo, useState } from 'react';

import { getPolicies, type PolicySummary } from '../api';
import {
  getLatestImpactAnalysis,
  runImpactAnalysis,
  type ImpactResponse,
  type ImpactRun,
} from './impactApi';

function eventValue(event: Event): string {
  return (event.currentTarget as HTMLElement & { value: string }).value;
}

function coverageLabel(run: ImpactRun): string {
  if (!run.current) return 'STALE — rerun required';
  if (run.status === 'queued') return 'QUEUED';
  if (run.status === 'running') return 'RUNNING';
  if (run.status === 'failed') return 'BLOCKED';
  if (run.coverage_state === 'too_large') return 'TOO LARGE FOR EXACT ANALYSIS';
  if (run.coverage_state === 'incomplete') return 'INCOMPLETE — BLOCKED';
  if (run.gate_passed) return 'EXACT IMPACT GATE PASSED';
  return 'ANALYSIS COMPLETE — REVIEW REQUIRED';
}

export function ImpactGate() {
  const [policies, setPolicies] = useState<PolicySummary[]>([]);
  const [policyId, setPolicyId] = useState('');
  const [impact, setImpact] = useState<ImpactResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedPolicy = useMemo(
    () => policies.find((policy) => policy.id === policyId) ?? null,
    [policies, policyId],
  );

  const refresh = useCallback(async (selectedId: string) => {
    if (!selectedId) {
      setImpact(null);
      return;
    }
    const result = await getLatestImpactAnalysis(selectedId);
    setImpact(result);
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getPolicies()
      .then(async (rows) => {
        if (!active) return;
        const drafts = rows.filter((policy) => policy.draft_version !== null);
        setPolicies(drafts);
        const first = drafts[0]?.id ?? '';
        setPolicyId(first);
        if (first) {
          const result = await getLatestImpactAnalysis(first);
          if (active) setImpact(result);
        }
      })
      .catch((reason: unknown) => {
        if (!active) return;
        setError(reason instanceof Error ? reason.message : 'Could not load impact gate.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const run = impact?.run;
    if (!policyId || !run || !['queued', 'running'].includes(run.status)) return undefined;
    const timer = window.setInterval(() => {
      void refresh(policyId).catch(() => undefined);
    }, 3000);
    return () => window.clearInterval(timer);
  }, [impact?.run, policyId, refresh]);

  const selectPolicy = useCallback(
    async (event: Event) => {
      const value = eventValue(event);
      setPolicyId(value);
      setImpact(null);
      setError(null);
      try {
        await refresh(value);
      } catch (reason: unknown) {
        setError(reason instanceof Error ? reason.message : 'Could not load impact analysis.');
      }
    },
    [refresh],
  );

  const runAnalysis = useCallback(async () => {
    if (!policyId) return;
    setRunning(true);
    setError(null);
    try {
      const started = await runImpactAnalysis(policyId);
      setImpact({ run: started.run, results: [] });
    } catch (reason: unknown) {
      setError(reason instanceof Error ? reason.message : 'Could not start impact analysis.');
    } finally {
      setRunning(false);
    }
  }, [policyId]);

  const run = impact?.run ?? null;
  const busy = running || run?.status === 'queued' || run?.status === 'running';

  return (
    <s-section heading="Store-wide Impact Gate">
      <s-stack direction="block" gap="base">
        <s-paragraph>
          Analyze a draft across every currently supported buyer-location and product-variant context
          before Atellius is ever allowed to publish it. This milestone is read-only and cannot change
          Shopify.
        </s-paragraph>

        {loading && <s-paragraph>Loading draft policies…</s-paragraph>}
        {!loading && policies.length === 0 && (
          <s-paragraph>Create and save a B2B Commerce draft rule before running impact analysis.</s-paragraph>
        )}

        {policies.length > 0 && (
          <>
            <s-select label="Draft to analyze" value={policyId} onChange={selectPolicy}>
              {policies.map((policy) => (
                <s-option key={policy.id} value={policy.id}>
                  {policy.name} · draft v{policy.draft_version}
                </s-option>
              ))}
            </s-select>
            <s-button onClick={() => void runAnalysis()} disabled={busy || !policyId}>
              {busy ? 'Impact analysis in progress' : 'Run exact impact analysis'}
            </s-button>
          </>
        )}

        {selectedPolicy && !run && !busy && (
          <s-paragraph>
            No impact run exists yet for {selectedPolicy.name}. Run the gate to measure the full
            supported effect of this draft.
          </s-paragraph>
        )}

        {run && (
          <s-stack direction="block" gap="small">
            <s-heading>{coverageLabel(run)}</s-heading>
            <s-paragraph>
              Draft v{run.policy_version} · {run.analyzed_contexts.toLocaleString()} of{' '}
              {run.planned_contexts.toLocaleString()} planned contexts analyzed
            </s-paragraph>
            <s-paragraph>
              {run.changed_contexts.toLocaleString()} changed ·{' '}
              {run.unchanged_contexts.toLocaleString()} unchanged ·{' '}
              {run.conflicted_contexts.toLocaleString()} conflicts ·{' '}
              {run.incomplete_contexts.toLocaleString()} incomplete
            </s-paragraph>

            {!run.current && (
              <s-paragraph>
                This result no longer matches the current draft, active Atellius rules, or latest
                Shopify sync. It cannot be used as a safety gate.
              </s-paragraph>
            )}

            {run.coverage_state === 'too_large' && (
              <s-paragraph>
                The exact universe contains {run.planned_contexts.toLocaleString()} contexts, above
                the current {run.analysis_limit.toLocaleString()}-context ceiling. Atellius stopped
                without sampling or claiming safety.
              </s-paragraph>
            )}

            {run.coverage_state === 'incomplete' && (
              <s-paragraph>
                At least one context could not be analyzed exhaustively. The gate remains blocked.
              </s-paragraph>
            )}

            {run.status === 'failed' && (
              <s-paragraph>Impact analysis is blocked: {run.error ?? 'Unknown analysis failure.'}</s-paragraph>
            )}

            {run.gate_passed && (
              <s-paragraph>
                Exact supported coverage is complete and conflict-free for this snapshot. Publishing
                is still disabled in M6; this result is only the prerequisite safety evidence.
              </s-paragraph>
            )}

            {(run.largest_increase_pct || run.largest_decrease_pct) && (
              <s-paragraph>
                Largest price increase: {run.largest_increase_pct ?? 'none'}% · largest decrease:{' '}
                {run.largest_decrease_pct ?? 'none'}%
              </s-paragraph>
            )}

            {impact?.results.slice(0, 10).map((result, index) => (
              <s-stack
                key={`${result.company_location_name}-${result.sku ?? result.variant_title}-${index}`}
                direction="block"
                gap="small"
              >
                <s-heading>
                  {result.company_name} — {result.company_location_name}
                </s-heading>
                <s-paragraph>
                  {result.product_title} · {result.sku || result.variant_title}
                </s-paragraph>
                <s-paragraph>
                  {result.status.toUpperCase()} · {result.quantities_tested.length} quantity boundaries
                  tested
                  {result.largest_delta_pct ? ` · max price delta ${result.largest_delta_pct}%` : ''}
                </s-paragraph>
                {result.error && <s-paragraph>{result.error}</s-paragraph>}
              </s-stack>
            ))}
          </s-stack>
        )}

        {error && <s-paragraph>Impact Gate needs attention: {error}</s-paragraph>}
      </s-stack>
    </s-section>
  );
}
